import { useEffect, useRef, useState } from "react";
import { sounds, useVolume } from "../audio/sounds";
import { useT } from "../i18n/context";
import { IconButton } from "./IconButton";
import { Icon } from "./icons";

/** Close the panel on a press outside it or on Escape. */
export function useDismiss(open: boolean, close: () => void) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("pointerdown", away);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", away);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);
  return root;
}

/**
 * The table's volume for this device (review 0013, decision 64 revised: a slider, so a
 * player in Discord's voice chat can turn only the game down). 0 is silence; the icon says
 * so. Letting go plays a card, so the level is heard, not guessed.
 */
export function SoundControl({ size }: { size: number }) {
  const { t } = useT();
  const volume = useVolume();
  const [open, setOpen] = useState(false);
  const root = useDismiss(open, () => setOpen(false));
  const percent = Math.round(volume * 100);
  const label = t("tooltip.sound");
  return (
    <div ref={root} className="relative">
      <IconButton label={label} size={size} onClick={() => setOpen((was) => !was)}>
        <Icon name={volume === 0 ? "soundOff" : "sound"} />
      </IconButton>
      {open ? (
        <div className="absolute left-1/2 top-[calc(100%+10px)] z-30 flex w-[230px] -translate-x-1/2 items-center gap-3 rounded-[16px] border-[1.5px] border-edge bg-panel-strong px-4 py-3 shadow-panel backdrop-blur-[10px]">
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={percent}
            aria-label={label}
            aria-valuetext={`${percent}%`}
            className="h-2 flex-1 cursor-pointer accent-gold"
            onChange={(event) => sounds.setVolume(Number(event.target.value) / 100)}
            onPointerUp={() => sounds.play("kart-at")}
            onKeyUp={() => sounds.play("kart-at")}
          />
          <span className="w-11 text-right text-[13px] font-bold tabular-nums">{percent}%</span>
        </div>
      ) : null}
    </div>
  );
}
